import { useTranslation } from "react-i18next";

import { Modal } from "@fragments";

import { CHAT_WIDTH, PREVIEW_WIDTH } from "./enums/layout";
import useGenerateVideoScript from "./hooks/useGenerateVideoScript";
import GenerateVideoScriptView from "./view";

/**
 * "Generate video script" — a conversation with platform-builder-server
 * about a content that has no video yet. Each message the creator sends
 * asks for a script (or an adjustment); each answer arrives as a card and
 * opens the script in a preview column beside the chat.
 *
 *   const ref = useRef();
 *   <GenerateVideoScriptModal modalRef={ref} />
 *   ref.current.open({ id: content.id, name: content.name });
 */
export default function GenerateVideoScriptModal({ modalRef }) {
    const { t } = useTranslation("contentModals");
    const {
        open,
        contentName,
        prompt,
        promptError,
        duration,
        durations,
        personality,
        personalities,
        status,
        messages,
        previewMessage,
        previewOpen,
        contextLine,
        steps,
        stepIndex,
        handlePromptChange,
        handleDurationChange,
        handlePersonalityChange,
        handleGenerate,
        handleRetry,
        handleOpenPreview,
        handleClosePreview,
        handleClose,
        handleCopy,
        handleDownloadPdf,
        handleDownloadDocx,
    } = useGenerateVideoScript({ modalRef });

    // The surface is the chat column alone, or the chat plus the preview
    // column; styles.scss transitions the width between the two.
    const size = previewOpen ? CHAT_WIDTH + PREVIEW_WIDTH : CHAT_WIDTH;

    return (
        <Modal
            open={open}
            onClose={handleClose}
            variant="flat"
            size={size}
            className="generate-script-modal"
            closeLabel={t("common:actions.close")}
        >
            <GenerateVideoScriptView
                contentName={contentName}
                prompt={prompt}
                promptError={promptError}
                duration={duration}
                durations={durations}
                personality={personality}
                personalities={personalities}
                status={status}
                messages={messages}
                previewMessage={previewMessage}
                previewOpen={previewOpen}
                contextLine={contextLine}
                steps={steps}
                stepIndex={stepIndex}
                onPromptChange={handlePromptChange}
                onDurationChange={handleDurationChange}
                onPersonalityChange={handlePersonalityChange}
                onGenerate={handleGenerate}
                onRetry={handleRetry}
                onOpenPreview={handleOpenPreview}
                onClosePreview={handleClosePreview}
                onCopy={handleCopy}
                onDownloadPdf={handleDownloadPdf}
                onDownloadDocx={handleDownloadDocx}
            />
        </Modal>
    );
}
